import type { BreakdownRow, Statement } from './waterfall/statement';
import { groupIdSeparator } from './waterfall/statement';

/**
 * Everything here runs at RENDER time only - when a PDF is generated, an Excel is exported or a table
 * is drawn - and never writes anything back. It lives directly under libs/model/src/lib (outside the
 * frozen waterfall/ folder) because the PDF cloud function cannot import an Angular lib, and the three
 * renderers must share one implementation or they drift apart.
 *
 * Why nothing here may be persisted:
 * (1) which rows are hidden is a live choice - the eye can be toggled at any time - so a number that
 *     depends on it can only be computed when the document is drawn;
 * (2) hiddenData stores row ids, and an id embeds the row's ORIGINAL position (getRowId() in
 *     libs/waterfall/.../statement/utils/styling.ts). A draft rebuilds its rows and their ids from the
 *     positions every time it is displayed: if compacted positions were ever stored and used as the
 *     source, the rebuilt ids would no longer match the ones hiddenData holds and hidden rows would
 *     silently come back.
 * So relabelBreakdownRows() returns NEW row objects, keeps `id` untouched, and is only ever applied to
 * the rows that survive hideRows(), after every id has been assigned.
 */

/** 1 -> a, 2 -> b ... 27 -> aa. Moved here from styling.ts so the render-time relabel uses the same letters. */
export function getLetter(number: number) {
  if (number <= 0) return '-';
  let letter = '';
  if (number > 26) letter = String.fromCharCode(64 + Math.floor(number / 26));
  const _number = number % 26;
  letter = letter + String.fromCharCode(64 + _number);
  return letter.toLowerCase();
}

/** A shadow row is the "recoupment" twin printed right before the "net profit" row of the same right; the pair shares one label. */
function isShadow(row: BreakdownRow | undefined) {
  return row?.percentConfig?.calculatedOn === 'shadow';
}

/**
 * Renumbers the rows that remain after hidden ones were removed, so the document shows no gaps.
 *
 * Three numberings live on a row, and each follows its own rule:
 * - `position` (the "#" column) is a depth in the rights tree, not a row counter: a group row and its
 *   members legitimately share one number. So the remaining DISTINCT values are ranked 1..n rather
 *   than counting rows ([1,2,3,3,4] with the 2 hidden becomes [1,2,2,3]).
 * - the "a. / b. / c." prefix of a horizontal group's members restarts on each `horizontal` row.
 * - the "1. / 2. / 3." prefix of a vertical group's members restarts on each `vertical` or
 *   `sub-vertical` row.
 * A shadow row and its twin keep sharing one letter or number, exactly as applyGroupLogic() assigns
 * them. A row whose section carries no prefix is left as it is: nothing is ever invented.
 *
 * Returns new objects and never touches `id`. With no hidden row the output equals the input.
 */
export function relabelBreakdownRows(rows: BreakdownRow[]): BreakdownRow[] {
  // Array.from, not [...set]: this file is also compiled by ts-node with tools/tsconfig.tools.json,
  // whose target is es5 - spreading a Set there fails with TS2802, which breaks `npm start`.
  const ranks = Array.from(new Set(rows.map(row => row.position).filter(position => !!position))).sort((a, b) => a - b);

  let letter = 0;
  let step = 0;
  return rows.map((row, index) => {
    const previous = rows[index - 1];
    const relabelled: BreakdownRow = { ...row };

    if (row.position) relabelled.position = ranks.indexOf(row.position) + 1;

    if (row.type === 'horizontal') letter = 0;
    if (row.type === 'vertical' || row.type === 'sub-vertical') step = 0;

    // Old statements stored before ids existed may carry rows without a section: leave those alone.
    if (row.type === 'right-horizontal' || row.type === 'sub-vertical') {
      if (!isShadow(previous)) letter++;
      if (typeof row.section === 'string') relabelled.section = row.section.replace(/^[a-z]+\. /, `${getLetter(letter)}. `);
    } else if (row.type === 'right-vertical' || row.type === 'right-sub-vertical') {
      if (!isShadow(previous)) step++;
      if (typeof row.section === 'string') relabelled.section = row.section.replace(/^\d+\. /, `${step}. `);
    }

    return relabelled;
  });
}

/** The two tables that carry a "#" column and lettered members; the other tables have no numbering to fix. */
const relabelledKeys = ['sourcesBreakdown', 'outgoingBreakdown', 'sourcesBreakdownIntermediary', 'outgoingBreakdownIntermediary'];

/**
 * The eye button always writes under the detailed key ('outgoingBreakdown' / 'sourcesBreakdown'): the
 * key is hard-coded in the two breakdown templates, even when the Standard display mode shows the
 * *Intermediary table on screen. The Standard PDF prints those *Intermediary tables and so ignored the
 * hidden rows. The detailed list is applied to the intermediary table too: rows that survive
 * anonymisation keep the same id in both. Known consequence: hiding a group ROW in detailed mode also
 * drops the merged row Standard mode builds from that group.
 */
const intermediaryOf: Record<string, string> = {
  sourcesBreakdown: 'sourcesBreakdownIntermediary',
  outgoingBreakdown: 'outgoingBreakdownIntermediary',
};

type ReportedData = Statement['reportedData'];
type HiddenRef = { id: string };

function withoutHidden<T extends { id?: string; rows?: BreakdownRow[] }>(items: T[], hidden: HiddenRef[], relabel: boolean): T[] {
  return items
    .filter(item => !hidden.some(h => h.id === item.id))
    .map(item => {
      if (!item?.rows?.length) return item;
      const rows = item.rows.filter(row => !hidden.some(h => h.id === row.id || h.id.includes(`${groupIdSeparator}${row.id}`)));
      return { ...item, rows: relabel ? relabelBreakdownRows(rows) : rows };
    });
}

/**
 * The reported data of a statement without its hidden tables and rows, renumbered where it matters.
 * Shared by the PDF generation and the Excel export; the on-screen tables apply the same rules through
 * the relabelVisibleRows pipe. Pure: `statement.reportedData` is never modified, so calling it twice
 * gives the same result.
 */
export function hideRows(statement: Statement): ReportedData {
  if (!statement.hiddenData) return statement.reportedData;
  const reportedData = { ...statement.reportedData };
  for (const key in statement.hiddenData) {
    const hidden = statement.hiddenData[key];
    // An empty list (a key the seed created, or a user who un-hid everything) must leave the document
    // strictly as it was: no filter, no renumbering. That is what keeps "nothing hidden" byte-for-byte
    // identical by construction, not by luck.
    if (!hidden?.length) continue;
    const keys = [key, intermediaryOf[key]].filter(k => !!k && reportedData[k]);
    for (const k of keys) {
      reportedData[k] = withoutHidden(reportedData[k], hidden, relabelledKeys.includes(k));
    }
  }
  return reportedData;
}
